import React, { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

export default function AdminCompliance() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("unreviewed"); // unreviewed | all

    const load = async () => {
        setLoading(true);
        try {
            const params = filter === "unreviewed" ? { reviewed: false } : {};
            const r = await api.get("/admin/compliance", { params });
            setItems(r.data || []);
        } catch {
            toast.error("Couldn't load compliance violations.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const markReviewed = async (id) => {
        try {
            await api.patch(`/admin/compliance/${id}`, { reviewed: true });
            toast.success("Marked reviewed.");
            setItems((prev) => prev.filter((x) => x.id !== id));
        } catch {
            toast.error("Couldn't update.");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-compliance">
            <header className="flex items-center justify-between">
                <div>
                    <div className="mm-eyebrow">Risk</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Jarvis Compliance</h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        Assistant turns (voice or text) that mentioned a carrier name or used guarantee phrasing. Review and mark resolved.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter("unreviewed")}
                        className={`rounded-full px-3 py-1.5 text-xs ${filter === "unreviewed" ? "bg-mm-navy text-white" : "bg-white text-mm-navy border border-mm-line"}`}
                        data-testid="compliance-filter-unreviewed"
                    >
                        Unreviewed
                    </button>
                    <button
                        onClick={() => setFilter("all")}
                        className={`rounded-full px-3 py-1.5 text-xs ${filter === "all" ? "bg-mm-navy text-white" : "bg-white text-mm-navy border border-mm-line"}`}
                        data-testid="compliance-filter-all"
                    >
                        All
                    </button>
                </div>
            </header>

            <div className="mm-card overflow-hidden">
                {loading ? (
                    <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center text-mm-muted text-sm">No violations to show.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>When</Th>
                                <Th>Session</Th>
                                <Th>Matched</Th>
                                <Th>Turn</Th>
                                <Th className="w-32">Action</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((v) => (
                                <tr key={v.id} className="border-t border-mm-line align-top" data-testid={`compliance-row-${v.id}`}>
                                    <Td className="whitespace-nowrap">{new Date(v.occurred_at).toLocaleString()}</Td>
                                    <Td className="font-mono text-xs">{v.session_id?.slice(0, 8)}</Td>
                                    <Td>
                                        <div className="flex flex-wrap gap-1">
                                            {(v.matched_terms || []).map((m) => (
                                                <span key={m} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[10px] text-rose-800">
                                                    <ShieldAlert size={10} /> {m}
                                                </span>
                                            ))}
                                        </div>
                                    </Td>
                                    <Td className="max-w-md whitespace-pre-wrap text-mm-ink">{v.text}</Td>
                                    <Td>
                                        {v.reviewed ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-1 text-[10px] text-emerald-800">
                                                <Check size={10} /> Reviewed
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => markReviewed(v.id)}
                                                className="rounded-full border border-mm-line bg-white px-2.5 py-1 text-[11px] text-mm-navy hover:bg-mm-bg"
                                                data-testid={`compliance-mark-reviewed-${v.id}`}
                                            >
                                                Mark reviewed
                                            </button>
                                        )}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Th({ children, className = "" }) { return <th className={`px-3 py-2.5 text-left font-medium ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>; }
